import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Plus, Pencil, Trash2, ListChecks, Award } from "lucide-react";
import { ICON_MAP, ICON_OPTIONS } from "@/utils/status";
import { useOutcomes } from "@/context/OutcomesContext";
import { useAuth } from "@/context/AuthContext";
import { Navigate } from "react-router-dom";

const PRESET_COLORS = ["#3B82F6", "#10B981", "#14B8A6", "#F59E0B", "#64748B", "#F43F5E", "#8B5CF6", "#EC4899", "#059669", "#EA580C"];

const TABS = [
  { key: "stati", label: "Stati Appuntamento", icon: ListChecks, kind: "outcome" },
  { key: "esiti-consulenti", label: "Esiti Consulenti", icon: Award, kind: "result", calendar: "consulenti" },
  { key: "esiti-trainers", label: "Esiti Trainers", icon: Award, kind: "result", calendar: "trainers" },
];

export default function OutcomesPage() {
  const { user } = useAuth();
  const { outcomes, results, refresh } = useOutcomes();
  const [tab, setTab] = useState("stati");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => { refresh(); }, [refresh]);

  if (user && user.role !== "admin") return <Navigate to="/" replace />;

  const currentTab = TABS.find((t) => t.key === tab);
  const isOutcome = currentTab.kind === "outcome";
  const items = isOutcome
    ? [...outcomes].sort((a, b) => a.order - b.order)
    : results.filter((r) => r.calendar === currentTab.calendar).sort((a, b) => a.order - b.order);

  const openNew = () => {
    const base = {
      key: "",
      label: "",
      color: isOutcome ? PRESET_COLORS[0] : "#10B981",
      icon: isOutcome ? "circle" : "check-circle",
      is_default: false,
      order: (items[items.length - 1]?.order || 0) + 10,
    };
    if (isOutcome) {
      base.is_completed = false;
    } else {
      base.calendar = currentTab.calendar;
    }
    setEditing(base);
    setOpen(true);
  };

  const openEdit = (o) => { setEditing({ ...o }); setOpen(true); };

  const save = async () => {
    try {
      if (!editing.key || !editing.label) {
        toast.error("Chiave e etichetta sono obbligatorie"); return;
      }
      const base = {
        key: editing.key.trim(),
        label: editing.label.trim(),
        color: editing.color,
        icon: editing.icon,
        is_default: !!editing.is_default,
        order: Number(editing.order) || 0,
      };
      const url = isOutcome ? "/outcome-types" : "/result-types";
      const payload = isOutcome
        ? { ...base, is_completed: !!editing.is_completed }
        : { ...base, calendar: editing.calendar };
      if (editing.id) await api.patch(`${url}/${editing.id}`, payload);
      else await api.post(url, payload);
      toast.success("Salvato");
      setOpen(false);
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (o) => {
    const kind = isOutcome ? "stato" : "esito";
    if (!window.confirm(`Eliminare ${kind} "${o.label}"?`)) return;
    try {
      const url = isOutcome ? "/outcome-types" : "/result-types";
      await api.delete(`${url}/${o.id}`);
      toast.success("Eliminato");
      refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="p-8 h-full overflow-y-auto">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight">Stati & Esiti</h1>
          <p className="text-muted-foreground text-sm mt-1">
            <strong>Stati</strong>: fase dell'appuntamento (unica per entrambi i calendari). <strong>Esiti</strong>: risultato business, attivi solo quando lo stato è "chiuso".
          </p>
        </div>
        <Button onClick={openNew} data-testid={`add-${isOutcome ? "outcome" : "result"}-button`}>
          <Plus className="h-4 w-4 mr-2" />Nuovo {isOutcome ? "Stato" : "Esito"}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="mb-4">
        <TabsList>
          {TABS.map((t) => {
            const Icon = t.icon;
            return (
              <TabsTrigger key={t.key} value={t.key} data-testid={`outcomes-tab-${t.key}`}>
                <Icon className="h-3.5 w-3.5 mr-1.5" />{t.label}
              </TabsTrigger>
            );
          })}
        </TabsList>
      </Tabs>

      <div className="bg-card border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-16">Ordine</TableHead>
              <TableHead>Etichetta</TableHead>
              <TableHead>Chiave</TableHead>
              <TableHead>Icona</TableHead>
              <TableHead>Colore</TableHead>
              <TableHead>Default</TableHead>
              {isOutcome && <TableHead>Chiuso</TableHead>}
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((o) => {
              const IconC = ICON_MAP[o.icon] || ICON_MAP.circle;
              return (
                <TableRow key={o.id} data-testid={`${isOutcome ? "outcome" : "result"}-row-${o.id}`}>
                  <TableCell className="font-mono text-xs">{o.order}</TableCell>
                  <TableCell className="font-medium">
                    <span
                      className="inline-flex items-center gap-2 px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wide"
                      style={{ backgroundColor: o.color, color: "#fff" }}
                    >
                      <IconC className="h-3.5 w-3.5" />{o.label}
                    </span>
                  </TableCell>
                  <TableCell className="font-mono text-xs text-muted-foreground">{o.key}</TableCell>
                  <TableCell className="text-muted-foreground text-xs">{o.icon}</TableCell>
                  <TableCell><span className="inline-block h-4 w-4 rounded-full" style={{ backgroundColor: o.color }} /></TableCell>
                  <TableCell>{o.is_default && <Badge className="bg-blue-600">Default</Badge>}</TableCell>
                  {isOutcome && <TableCell>{o.is_completed && <Badge className="bg-emerald-600">Chiuso</Badge>}</TableCell>}
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" onClick={() => openEdit(o)} data-testid={`edit-${isOutcome ? "outcome" : "result"}-${o.id}`}><Pencil className="h-4 w-4" /></Button>
                    {!o.is_default && (
                      <Button variant="ghost" size="icon" onClick={() => remove(o)} className="text-rose-600" data-testid={`delete-${isOutcome ? "outcome" : "result"}-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
            {items.length === 0 && (
              <TableRow>
                <TableCell colSpan={isOutcome ? 8 : 7} className="text-center text-muted-foreground py-8">
                  Nessun {isOutcome ? "stato" : "esito"} configurato.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">
              {editing?.id ? "Modifica" : "Nuovo"} {isOutcome ? "Stato" : "Esito"}
            </DialogTitle>
          </DialogHeader>
          {editing && (
            <div className="space-y-3">
              {!isOutcome && (
                <div>
                  <Label>Calendario</Label>
                  <Tabs value={editing.calendar} onValueChange={(v) => setEditing({ ...editing, calendar: v })}>
                    <TabsList className="grid grid-cols-2 w-full">
                      <TabsTrigger value="consulenti">Consulenti</TabsTrigger>
                      <TabsTrigger value="trainers">Trainers</TabsTrigger>
                    </TabsList>
                  </Tabs>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Etichetta</Label>
                  <Input value={editing.label} onChange={(e) => setEditing({ ...editing, label: e.target.value })} data-testid="outcome-label-input" placeholder={isOutcome ? "Es. Effettuato" : "Es. Chiuso in Prima"} />
                </div>
                <div>
                  <Label>Chiave <span className="text-muted-foreground font-normal">(id interno)</span></Label>
                  <Input value={editing.key} onChange={(e) => setEditing({ ...editing, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })} data-testid="outcome-key-input" placeholder={isOutcome ? "effettuato" : "chiuso_prima"} />
                </div>
              </div>
              <div>
                <Label>Colore</Label>
                <div className="flex flex-wrap gap-2 mt-1">
                  {PRESET_COLORS.map((c) => (
                    <button key={c} type="button" onClick={() => setEditing({ ...editing, color: c })}
                      className={`h-8 w-8 rounded-full ${editing.color === c ? "ring-2 ring-offset-2 ring-slate-900" : ""}`}
                      style={{ backgroundColor: c }} />
                  ))}
                  <Input type="color" value={editing.color} onChange={(e) => setEditing({ ...editing, color: e.target.value })} className="h-8 w-14 p-1" />
                </div>
              </div>
              <div>
                <Label>Icona</Label>
                <div className="grid grid-cols-7 gap-2 mt-1">
                  {ICON_OPTIONS.map((k) => {
                    const IconC = ICON_MAP[k];
                    const active = editing.icon === k;
                    return (
                      <button key={k} type="button" onClick={() => setEditing({ ...editing, icon: k })}
                        className={`h-9 rounded-md flex items-center justify-center border ${active ? "text-white" : "text-slate-600 hover:bg-slate-50"}`}
                        style={active ? { backgroundColor: editing.color, borderColor: editing.color } : {}}>
                        <IconC className="h-4 w-4" />
                      </button>
                    );
                  })}
                </div>
              </div>
              <div className={`grid ${isOutcome ? "grid-cols-3" : "grid-cols-2"} gap-3`}>
                <div>
                  <Label>Ordine</Label>
                  <Input type="number" value={editing.order} onChange={(e) => setEditing({ ...editing, order: e.target.value })} />
                </div>
                <div className="flex items-end gap-2 pb-1">
                  <Switch checked={!!editing.is_default} onCheckedChange={(v) => setEditing({ ...editing, is_default: v })} />
                  <span className="text-sm">Default</span>
                </div>
                {isOutcome && (
                  <div className="flex items-end gap-2 pb-1">
                    <Switch checked={!!editing.is_completed} onCheckedChange={(v) => setEditing({ ...editing, is_completed: v })} />
                    <span className="text-sm">Attiva Esiti</span>
                  </div>
                )}
              </div>
              {isOutcome && (
                <p className="text-xs text-muted-foreground">
                  Attiva "Attiva Esiti" per gli stati che rappresentano un appuntamento chiuso/effettuato — solo per questi si potrà scegliere un Esito Appuntamento.
                </p>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Annulla</Button>
            <Button onClick={save} data-testid="save-outcome-button">Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
